import { Injectable } from '@angular/core';
import { MessageNotificationService } from 'message-notification-service-lib';
import { concatMap } from 'rxjs/operators';
import { FieldContext } from '../utils/fieldcontext';
import { Tag } from '../utils/tag';
import { XPath } from '../utils/xpath-utils';
import { AiAssistFunctionService } from './ai-assist-function.service';
import { ApplicationStoreService, INamespacePart, INamespaceSettings } from './application-store.service';
import { ContentControlToFieldMapService } from './content-control-to-field-map.service';
import { ContentControlService } from './content-control.service';
import { ActiveContentControlMonitor } from './document-monitors/active-content-control-monitor';
import { ContentControlMonitor } from './document-monitors/content-control-monitor';
import { DopePartService } from './dope-part.service';
import { ImageFieldsService } from './image-fields.service';
import { LoggingService } from './logging.service';
import { ModelPartService } from './model-part.service';
import { ServerModelService } from './server-model.service';
import { WordService } from './word.service';

@Injectable({ providedIn: 'root' })
export class ControllerService {
  constructor(
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private applicationStoreService: ApplicationStoreService,
    private contentControlMonitor: ContentControlMonitor,
    private contentControlService: ContentControlService,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private dopePartService: DopePartService,
    private imageFieldsService: ImageFieldsService,
    private loggingService: LoggingService,
    private messageNotificationService: MessageNotificationService,
    private modelPartService: ModelPartService,
    private serverModelService: ServerModelService,
    private wordService: WordService,
    private aiAssistFunctionService: AiAssistFunctionService
  ) {
    this.subscribeToContentControlDeletedEvents();
  }

  async update(wordContext: Word.RequestContext, fieldContext: FieldContext): Promise<FieldContext> {
    this.loggingService.log('ControllerService', 'update', fieldContext);
    const nss = await this.getOrCreateNamespaceSettings(fieldContext.namespace);
    const nsp = await this.applicationStoreService.getNamespacePart(nss);
    const currentContentControl = await this.activeContentControlMonitor.getActiveContentControl(wordContext);
    if (currentContentControl) {
      fieldContext = await this.imageFieldsService.update(fieldContext);

      const tag = Tag.fromString(currentContentControl.tag);
      let currentXpath;
      if (fieldContext.isImageField) {
        currentXpath = tag.id;
      } else {
        currentXpath = await this.dopePartService.getPath(tag, nsp);
      }
      await this.modelPartService.update(nsp.model, currentXpath!, fieldContext);
      await this.contentControlService.update(wordContext, fieldContext, currentContentControl);
      await this.contentControlToFieldMapService.set(currentContentControl.id, fieldContext);
      await this.serverModelService.insert(fieldContext);
    } else {
      this.loggingService.error('ControllerService', 'update', 'Trying to update a field without an active control control');
    }

    return fieldContext;
  }

  async insert(wordContext: Word.RequestContext, fieldContext: FieldContext): Promise<FieldContext> {
    this.loggingService.log('ControllerService', 'insert', fieldContext);
    const nss = await this.getOrCreateNamespaceSettings(fieldContext.namespace);
    const nsp = await this.applicationStoreService.getNamespacePart(nss);

    // image fields can change the final field path - we must do this before adding to the model
    fieldContext = await this.imageFieldsService.insert(fieldContext);
    fieldContext = await this.aiAssistFunctionService.insert(fieldContext);

    const refId = await this.applicationStoreService.incrementRefId();
    const { ancestorXpath, parentXpath } = await this.getInsertContext(wordContext, nsp, fieldContext);
    const xpath = await this.modelPartService.add(nsp.model, ancestorXpath, parentXpath, fieldContext, refId);

    let contentControlRefTag;
    if (fieldContext.isImageField) {
      contentControlRefTag = `kp:image=${xpath}`;
    } else {
      contentControlRefTag = await this.dopePartService.add(nsp, fieldContext, xpath, refId);
    }

    const contentControlId = await this.contentControlService.insert(wordContext, fieldContext, contentControlRefTag);
    await this.serverModelService.insert(fieldContext);
    await this.contentControlToFieldMapService.set(contentControlId, fieldContext);

    return fieldContext;
  }

  async insertFromPaste(wordContext: Word.RequestContext, fieldContext: FieldContext, targetContentControlId: number): Promise<void> {
    const pastedControl = await this.wordService.getContentControl(wordContext, targetContentControlId);
    if (!pastedControl) {
      throw new Error('Could not find pasted control.');
    }

    const nss = await this.getOrCreateNamespaceSettings(fieldContext.namespace);
    const nsp = await this.applicationStoreService.getNamespacePart(nss);

    fieldContext = await this.imageFieldsService.insert(fieldContext);
    fieldContext = await this.aiAssistFunctionService.insert(fieldContext);

    const refId = await this.applicationStoreService.incrementRefId();
    const { ancestorXpath, parentXpath } = await this.getPasteContext(wordContext, nsp, pastedControl);
    const xpath = await this.modelPartService.add(nsp.model, ancestorXpath, parentXpath, fieldContext, refId);

    if (fieldContext.isImageField) {
      pastedControl.tag = `kp:image=${xpath}`;
      this.contentControlService.replacePlaceholderPicture(fieldContext, pastedControl);
    } else {
      pastedControl.tag = await this.dopePartService.add(nsp, fieldContext, xpath, refId);
    }

    await this.serverModelService.insert(fieldContext);
    await this.contentControlToFieldMapService.set(targetContentControlId, fieldContext);
  }

  async getEntityForNamespace(namespace: string): Promise<string | undefined> {
    const nss = this.applicationStoreService.getNamespaceSettings(namespace);
    if (!nss) {
      return undefined;
    }

    const nsp = await this.applicationStoreService.getNamespacePart(nss);
    return await this.modelPartService.getTargetEntity(nsp.model);
  }

  async getOrCreateNamespaceSettings(namespace: string): Promise<INamespaceSettings> {
    let nss = this.applicationStoreService.getNamespaceSettings(namespace);
    if (!nss) {
      const model = await this.modelPartService.create(namespace);
      const dopeParts = await this.dopePartService.create(namespace, model.id);
      const serverModel = this.serverModelService.create(namespace);
      nss = {
        name: namespace,
        modelId: model.id,
        dopeXPathsId: dopeParts.xpathsPart.id,
        dopeConditionsId: dopeParts.conditionsPart.id,
        jsonModel: serverModel,
      };
      await this.applicationStoreService.setNamespaceSettings(namespace, nss);
    }

    return nss;
  }

  private async getInsertContext(
    wordContext: Word.RequestContext,
    nsp: INamespacePart,
    fieldContext: FieldContext
  ): Promise<{ ancestorXpath: XPath | null; parentXpath: XPath | null }> {
    let ancestorXpath: XPath | null = null;
    let parentXpath: XPath | null = null;
    const parentContentControl = await this.activeContentControlMonitor.getActiveContentControl(wordContext);

    // ToDo: use Tag class? Tag.fromString(parentContentControl.tag)
    if (parentContentControl?.tag?.startsWith('od:condition')) {
      return { ancestorXpath, parentXpath };
    }

    if (parentContentControl) {
      parentXpath = await this.dopePartService.getPath(Tag.fromString(parentContentControl.tag), nsp);

      // we don't need to do this if the field being inserted is an entityField
      if (!fieldContext.isEntityField) {
        const containerControls = await this.wordService.getContainers(wordContext, parentContentControl.id);
        const containerFields = this.contentControlToFieldMapService.getAll(containerControls.map((cc) => cc.id));

        const parentFieldPath = fieldContext.tailless();
        const ancestorContainerField = containerFields.find(
          (sfc) => sfc.path === parentFieldPath && sfc.entity === fieldContext.entity.entity && sfc.namespace === fieldContext.namespace
        );
        if (ancestorContainerField) {
          const ancestorContentControl = containerControls.find((cc) => cc.id === ancestorContainerField.contentControlId);
          ancestorXpath = await this.dopePartService.getPath(Tag.fromString(ancestorContentControl!.tag), nsp);
        }
      }
    }

    if (ancestorXpath !== parentXpath) {
      console.warn('Ancestor and Parent are different: ', ancestorXpath, parentXpath);
    }

    return { ancestorXpath, parentXpath };
  }

  private async getPasteContext(
    wordContext: Word.RequestContext,
    nsp: INamespacePart,
    pastedControl: Word.ContentControl
  ): Promise<{ ancestorXpath: XPath | null; parentXpath: XPath | null }> {
    let parentXpath: XPath | null = null;
    let ancestorXpath: XPath | null = null;

    if (!pastedControl.parentContentControlOrNullObject.isNullObject) {
      const parentOfPastedControl = await this.wordService.getContentControl(
        wordContext,
        pastedControl.parentContentControlOrNullObject.id
      );
      if (parentOfPastedControl?.tag?.startsWith('od:condition')) {
        return { ancestorXpath, parentXpath };
      }
      if (parentOfPastedControl) {
        parentXpath = await this.dopePartService.getPath(Tag.fromString(parentOfPastedControl.tag), nsp);
      }
    }
    // TODO: will this always be like this?
    ancestorXpath = parentXpath;
    return { ancestorXpath, parentXpath };
  }

  private subscribeToContentControlDeletedEvents(): void {
    this.contentControlMonitor.contentControlsDeleted$
      .pipe(concatMap((event) => this.contentControlDeleted({ id: event.id, tagRefId: event.tag })))
      .subscribe();
  }

  private async contentControlDeleted(tagValue: { id: number; tagRefId: string }): Promise<void> {
    this.loggingService.log('ControllerService', 'contentControlDeleted', tagValue);

    // tag is not recognised as having been created by the add-in
    const tag = this.parseTag(tagValue.tagRefId);
    if (!tag) {
      return;
    }

    await Word.run(async (context) => {
      try {
        const namespaces = this.applicationStoreService.getNamespaces();
        for (let i = 0; i < namespaces.length; i++) {
          const nss = await this.applicationStoreService.getNamespaceSettings(namespaces[i])!;
          const nsp = await this.applicationStoreService.getNamespacePart(nss);
          if (tag.type !== 'image') {
            const tagBelongsToPart = await this.dopePartService.hasPath(tag, nsp);
            if (tagBelongsToPart) {
              const xpath = await this.dopePartService.getPath(tag, nsp);
              await this.dopePartService.removePath(tag, nsp);
              await this.modelPartService.removePath(xpath, nsp.model);
              break;
            }
          } else {
            // image fields set the id to the xpath
            const xpath = tag.id;
            await this.modelPartService.removePath(xpath, nsp.model);
          }
        }
        await this.contentControlToFieldMapService.delete(tagValue.id);
      } catch (error) {
        this.messageNotificationService.error('Failed to update internal models.', error);
      }
      await context.sync();
    });
  }

  private parseTag(tagValue: string): Tag | null {
    try {
      return Tag.fromString(tagValue);
    } catch (e) {
      return null;
    }
  }
}
